<template>
  <router-view />
</template>

<script lang="ts" setup>
import { type ColorSchemePreference, useGlobalStore } from '@editor/ui-utils';
import { useQuasar } from 'quasar';
import { onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';

import { useOnlineCheck } from '@/composables/useOnlineCheck';

import useAbTestNotifications from './composables/useAbTestNotifications';
import usePing from './composables/usePing';
import featherIcons from './featherIcons';

const $q = useQuasar();
const globalStore = useGlobalStore();

// Apply the stored preference via Quasar Dark; 'system' -> 'auto' (tracks the OS).
const applyColorScheme = (preference: ColorSchemePreference): void => {
  $q.dark.set(preference === 'system' ? 'auto' : preference === 'dark');
};
// Run in setup so the theme is right on first paint.
applyColorScheme(globalStore.colorScheme);
watch(() => globalStore.colorScheme, applyColorScheme);
const customIcons = [
  'eye',
  'check-circle',
  'minus-circle',
  'cat-tool',
  'aissist',
  'gallery',
  'grid-customer-list',
  'javascript',
  'nested-navigation',
  'video-performance',
  'flag-de',
  'flag-en',
  'flag-it',
  'schedule-lock',
  'ai-generated-icon',
  'ai-prompt',
  'reschedule-save',
  'save-push',
  'publish-push',
  'cta',
  'recipe',
  'user-profile',
  'report-problem',
];

// Add support for feather icons
$q.iconMapFn = (icon: string) => {
  if (customIcons.includes(icon)) {
    return {
      icon: `svguse:/custom.svg#${icon}`,
    };
  }
  const i = featherIcons[icon];
  if (i) {
    return {
      icon: i,
    };
  }
};

onMounted(() => {
  usePing();
  useAbTestNotifications();
  useOnlineCheck();
});
</script>
<style lang="scss" src="./moduleLoader.scss" />
<style lang="scss">
@import '@quasar/extras/material-icons/material-icons.css';
@import '@quasar/extras/roboto-font/roboto-font.css';
@import 'quasar/src/css/index.sass';
@import '@editor/ui-utils';
@import '@editor/ui-layout';
@import './scss/main';
@import './scss/dark-shell';
@import './scss/override';
@import './scss/variables';
</style>
